/**
 * Base machinery shared by every `<caos-*>` custom element.
 *
 * Each component is a genuine custom element with an open shadow root. Styles are
 * carried on a **constructable stylesheet** adopted into the shadow root — no inline
 * `<style>` string is injected, so a strict `style-src 'self'` CSP is satisfied
 * without `'unsafe-inline'`. Components never hard-code a colour, font, space, or
 * radius: every visual value reads a `--caos-*` / `--brand-*` token, which inherits
 * across the shadow boundary (CAOS-0947). That is what makes the Atlantis theme and
 * any tenant's five-token white-label re-skin every component with zero per-component
 * override.
 */

/** Build a constructable stylesheet once, to be adopted by every instance. */
export function makeSheet(cssText: string): CSSStyleSheet {
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(cssText);
  return sheet;
}

/**
 * A `:host`-scoped reset every component adopts first: predictable box model,
 * inherit the token-driven font, and the shared hiding rules. Anything visual still
 * comes from a token.
 *
 * CAOS-581 — the hidden attribute MUST mean hidden, on the host AND on anything inside
 * the shadow root. The browser's own `[hidden]` rule is a USER-AGENT rule, so any author
 * rule setting `display` on the same element beats it, and hiding that element silently
 * does nothing. That is not theoretical: the account menu set `hidden` on "Edit this
 * page" while its own `.menu-item { display: flex }` kept it on screen and clickable —
 * a dead control that a test reading `el.hidden` reported as gone.
 *
 * It is declared HERE, in the reset every component adopts before its own sheet, rather
 * than once per component. Declaring it per component is the same bug waiting on the
 * next component: a new one that sets `display` on something it hides reopens the gap,
 * and nothing catches it. `!important` in the reset outranks a normal author `display`
 * whatever order the sheets adopt in, so a component cannot opt out by accident.
 *
 * ONE PLACE STILL HAS TO OPT IN: a shadow root the base class did not create. Three
 * components hang their panel off `document.body` in a root of their own — the app
 * launcher, the navigation gadget, the object caret — and set that root's stylesheets
 * directly. A root only carries what it is handed, so each adopts RESET alongside its
 * panel sheet. Any new root created that way must do the same; `hidden-guard.test.ts`
 * reads the source and fails if one does not.
 */
export const RESET = makeSheet(`
  :host {
    box-sizing: border-box;
    font-family: var(--caos-font);
    -webkit-font-smoothing: antialiased;
  }
  :host([hidden]) { display: none !important; }
  [hidden] { display: none !important; }
  *, *::before, *::after { box-sizing: border-box; }
`);

/** Register a custom element exactly once (safe under HMR / double-import). */
export function define(tag: string, ctor: CustomElementConstructor): void {
  if (!customElements.get(tag)) {
    customElements.define(tag, ctor);
  }
}

/**
 * The ONE rule every floating-panel-building entry point checks before it builds: a control that is
 * not on the document must never get a panel on `document.body`. The connect/data-in path already
 * enforced this (e.g. `caos-overflow-menu`'s `render()`), because building off-DOM strands one
 * orphaned panel per discarded row with no `disconnectedCallback` ever coming to remove it. The
 * OPEN path — the method a click or a direct public call actually invokes to show the panel — is the
 * other door onto the same floor and must answer the same way, not reinvent its own check (CAOS-632).
 */
export function canOpenFloatingPanel(el: Element): boolean {
  return el.isConnected;
}

/** Base class: attaches an open shadow root and adopts the shared reset + own sheet. */
export abstract class CaosElement extends HTMLElement {
  protected readonly root: ShadowRoot;

  protected constructor(sheet: CSSStyleSheet) {
    super();
    this.root = this.attachShadow({ mode: 'open' });
    this.root.adoptedStyleSheets = [RESET, sheet];
  }
}
